import { Fragment, useMemo, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useIsMutating } from '@tanstack/react-query';

import { ConfirmModal } from 'components/ConfirmModal';
import { Icon } from 'components/Icon';
import {
  MOBILE_HEADER_ICON_SIZE,
  mobileHeaderIconButtonClass,
} from 'components/Header/styles';
import {
  useOrganizationScopeOrNull,
  type OrganizationScopeContextValue,
} from 'components/OrganizationScope/context';
import { UnreadBadge } from 'components/SideBar/UnreadBadge';
import { organizationStrings } from 'constants/strings';
import { useMe } from 'hooks/useMe';
import {
  toUnreadCountByOrganization,
  useNotificationSummary,
} from 'hooks/useNotificationSummary';
import useToast from 'hooks/useToast';
import type { TMeMembership } from 'types';
import { cn } from 'utils/cn';

type OrganizationSwitcherProps = {
  variant?: 'desktop' | 'mobile';
  /** 所属が読めるまでの表示名。AppLayout が選択中組織の名前を渡す */
  organizationName?: string;
};

export function OrganizationSwitcher({
  variant = 'desktop',
  organizationName,
}: OrganizationSwitcherProps) {
  const scope = useOrganizationScopeOrNull();

  if (!scope) {
    return variant === 'desktop' ? (
      <StaticOrganizationLabel organizationName={organizationName} />
    ) : null;
  }

  return (
    <OrganizationSwitcherMenu
      scope={scope}
      variant={variant}
      organizationName={organizationName}
    />
  );
}

function StaticOrganizationLabel({
  organizationName,
}: {
  organizationName?: string;
}) {
  if (!organizationName) return null;

  return (
    <>
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-ink text-xs font-bold text-white">
        {organizationName.charAt(0)}
      </span>
      <span className="min-w-0 truncate text-sm font-semibold text-ink">
        {organizationName}
      </span>
    </>
  );
}

function OrganizationSwitcherMenu({
  scope,
  variant,
  organizationName,
}: OrganizationSwitcherProps & { scope: OrganizationScopeContextValue }) {
  const { organizationId, isBusy, switchOrganization } = scope;
  const { data: me } = useMe();
  const { data: summary } = useNotificationSummary();
  const isMutating = useIsMutating();
  const { toastWarning } = useToast();
  const [isSwitching, setIsSwitching] = useState(false);
  const [pendingMembership, setPendingMembership] =
    useState<TMeMembership | null>(null);

  const memberships = me?.memberships ?? [];
  const unreadCountByOrganizationId = useMemo(
    () => toUnreadCountByOrganization(summary),
    [summary],
  );
  // 選択中組織の未読はハンバーガー側のバッジに出るため、モバイルのトリガーは他組織分だけを示す
  const otherUnreadTotal = memberships.reduce((total, membership) => {
    if (membership.organizationId === organizationId) return total;
    return total + (unreadCountByOrganizationId.get(membership.organizationId) ?? 0);
  }, 0);

  const isDisabled = isBusy || isSwitching || isMutating > 0;
  const currentMembership = memberships.find(
    (membership) => membership.organizationId === organizationId,
  );
  const currentName = currentMembership?.organizationName ?? organizationName;

  const handleSelect = async (membership: TMeMembership): Promise<void> => {
    if (membership.organizationId === organizationId) return;

    setIsSwitching(true);
    const status = await switchOrganization(membership.organizationId);
    setIsSwitching(false);

    if (status === 'busy') {
      toastWarning(organizationStrings.switcher.busy);
      return;
    }

    if (status === 'unsaved-input') {
      setPendingMembership(membership);
    }
  };

  const handleDiscardAndSwitch = async (): Promise<void> => {
    if (!pendingMembership) return;

    setIsSwitching(true);
    const status = await switchOrganization(pendingMembership.organizationId, {
      discardUnsavedInput: true,
    });
    setIsSwitching(false);

    if (status === 'busy') {
      toastWarning(organizationStrings.switcher.busy);
      return;
    }

    setPendingMembership(null);
  };

  if (memberships.length <= 1) {
    return variant === 'desktop' ? (
      <StaticOrganizationLabel organizationName={currentName} />
    ) : null;
  }

  return (
    <>
      {/* モバイルはトップバーを基準に置く（ボタン基準だと幅 288px のメニューが狭い画面で左にはみ出す） */}
      <Menu as="div" className={cn('min-w-0', variant === 'desktop' && 'relative')}>
        <Menu.Button
          disabled={isDisabled}
          aria-label={
            variant === 'mobile' ? organizationStrings.switcher.label : undefined
          }
          className={cn(
            'group relative flex items-center text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:cursor-not-allowed disabled:opacity-60',
            variant === 'mobile'
              ? `${mobileHeaderIconButtonClass} shrink-0 justify-center rounded-2xl text-ink hover:bg-neutral-25`
              : 'min-w-0 gap-3 rounded-[var(--btn-radius)] px-2 py-1.5 hover:bg-neutral-25',
          )}
        >
          {variant === 'mobile' ? (
            <>
              <Icon
                icon="chevronsRight"
                width={MOBILE_HEADER_ICON_SIZE}
                height={MOBILE_HEADER_ICON_SIZE}
              />
              <UnreadBadge count={otherUnreadTotal} className="absolute right-0.5 top-0.5" />
            </>
          ) : (
            <>
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-ink text-xs font-bold text-white">
                {currentName?.charAt(0)}
              </span>
              <span className="min-w-0 truncate text-sm font-semibold text-ink">
                {currentName}
              </span>
              <Icon
                icon="chevronDown"
                className="h-4 w-4 shrink-0 text-muted transition group-hover:text-ink"
              />
            </>
          )}
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items
            className={cn(
              'absolute z-30 mt-2 w-72 origin-top-right rounded-2xl border border-border/70 bg-white p-1.5 shadow-lg focus:outline-none',
              variant === 'mobile'
                ? 'right-3 top-full max-w-[calc(100%-1.5rem)]'
                : 'left-0',
            )}
          >
            <p className="px-3 py-2 text-[11px] font-semibold text-muted">
              {organizationStrings.switcher.label}
            </p>
            {memberships.map((membership) => {
              const isCurrent = membership.organizationId === organizationId;
              const unreadCount =
                unreadCountByOrganizationId.get(membership.organizationId) ?? 0;

              return (
                <Menu.Item key={membership.organizationId} disabled={isDisabled}>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={() => handleSelect(membership)}
                      aria-current={isCurrent ? 'true' : undefined}
                      aria-label={
                        isCurrent
                          ? undefined
                          : organizationStrings.switcher.switchToLabel(
                              membership.organizationName,
                            )
                      }
                      disabled={isDisabled}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm transition disabled:cursor-not-allowed disabled:opacity-60',
                        active && !isCurrent && 'bg-neutral-25',
                      )}
                    >
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-ink text-[11px] font-bold text-white">
                        {membership.organizationName.charAt(0)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-semibold text-ink">
                          {membership.organizationName}
                        </span>
                        {isCurrent && (
                          <span className="block text-[11px] text-muted">
                            {organizationStrings.switcher.currentLabel}
                          </span>
                        )}
                      </span>
                      {unreadCount > 0 && (
                        <>
                          <UnreadBadge count={unreadCount} />
                          <span className="sr-only">
                            {organizationStrings.unreadLabel(unreadCount)}
                          </span>
                        </>
                      )}
                    </button>
                  )}
                </Menu.Item>
              );
            })}
            {isDisabled && (
              <p className="px-3 py-2 text-[11px] text-muted">
                {organizationStrings.switcher.busy}
              </p>
            )}
          </Menu.Items>
        </Transition>
      </Menu>

      <ConfirmModal
        open={pendingMembership !== null}
        textTitle={organizationStrings.switcher.unsavedTitle}
        textMessage={organizationStrings.switcher.unsavedMessage}
        textCancel={organizationStrings.switcher.unsavedCancel}
        textConfirm={organizationStrings.switcher.unsavedConfirm}
        isLoading={isSwitching}
        onClose={() => setPendingMembership(null)}
        onConfirm={() => void handleDiscardAndSwitch()}
      />
    </>
  );
}
